import { cn, DropdownMenu } from "@win-reality/react-ui";
import { ArrowLeft, EllipsisVertical, Loader2, TrendingUp } from "lucide-react";
import { ReactNode } from "react";

import { CONTENT_WIDTH } from "../../constants/layout";
import { useTaggingTheme } from "../../hooks/useTaggingTheme";
import { GamePlayerRef, HitData, PitchData, Sport } from "../../types/gameFull";
import { getStrikeZoneBall } from "../../utils/pitchTagging";
import { formatMph } from "../../utils/plateAppearanceFeed";
import { formatPlayerName } from "../../utils/playerName";
import { LandingPointVis } from "../Visualizations/LandingPointVis";
import { SoftballLandingPointVis } from "../Visualizations/SoftballLandingPointVis";
import { StrikeZone } from "./StrikeZone";

type PitchHeaderProps = {
  pitcher: GamePlayerRef | null;
  hitter: GamePlayerRef | null;
  pitcherColor: string;
  hitterColor: string;
  /** Label of the pitch type picked in the form (or its saved/suggested one) */
  pitchType: string | null;
  pitchData: PitchData | null;
  hitData: HitData | null;
  sport: Sport;
  isWaiting: boolean;
  view: "tags" | "metrics";
  onToggleView: () => void;
  onEndGame: () => void;
};

/** Fixed top of the Pitch Details panel: who faced the pitch and where it went. */
export const PitchHeader = ({
  pitcher,
  hitter,
  pitcherColor,
  hitterColor,
  pitchType,
  pitchData,
  hitData,
  sport,
  isWaiting,
  view,
  onToggleView,
  onEndGame,
}: PitchHeaderProps) => {
  const { theme } = useTaggingTheme();
  const isSoftball = sport === "softball";
  const ball = getStrikeZoneBall(pitchData, "pitcher", isSoftball);
  const LandingVis = isSoftball ? SoftballLandingPointVis : LandingPointVis;

  return (
    <div
      data-testid="pitch-tagging-header-panel"
      className={cn(
        // Layout: sizes the content's padding to the panel, not the window
        "@container",
        // Background: matches the sidebar, setting it apart from the tags
        "bg-tagging-sidebar-bg",
        // Effects: raised so its shadow falls on the scrolling tag sections
        "relative z-[1] shadow-[0_4px_4px_0_rgba(0,0,0,0.25)] transition-colors",
      )}
    >
      {/* Same padding as the tag cards' container, so the content lines up
          with the cards' edges. Until the panel is wide enough to leave a
          margin beside the max width (750px + room for the menu on each
          side), the right side makes room for the menu instead */}
      <div
        className={cn(
          CONTENT_WIDTH,
          // Layout
          "flex items-center justify-between gap-6",
          // Spacing
          "px-6 py-3 @max-[51rem]:pr-10",
        )}
      >
        <div className="min-w-0 space-y-4">
          <div className="space-y-1.5 text-sm font-bold uppercase leading-tight">
            <PlayerLine
              data-testid="pitch-tagging-pitcher-value"
              label="Pitcher"
              color={pitcherColor}
              player={pitcher}
            />
            <PlayerLine
              data-testid="pitch-tagging-hitter-value"
              label="Hitter"
              color={hitterColor}
              player={hitter}
            />
          </div>

          {isWaiting ? (
            <div
              data-testid="pitch-tagging-waiting-status"
              className="text-sm italic text-tagging-muted"
            >
              Waiting on pitch...
            </div>
          ) : (
            <PitchInfo
              pitchType={pitchType}
              pitchVelocity={pitchData?.RelSpeedMPH ?? null}
              exitVelocity={hitData?.ExitSpeedMPH ?? null}
            />
          )}

          {!isWaiting && (
            <button
              type="button"
              data-testid="pitch-tagging-view-metrics-button"
              aria-pressed={view === "metrics"}
              onClick={onToggleView}
              className={cn(
                // Layout
                "flex items-center gap-2",
                // Text
                "text-sm font-semibold text-tagging-accent",
                // States
                "hover:underline",
              )}
            >
              {view === "metrics" ? (
                <>
                  <ArrowLeft className="h-4 w-4" />
                  Back to Tags
                </>
              ) : (
                <>
                  <TrendingUp className="h-4 w-4" />
                  View Metrics
                </>
              )}
            </button>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-6">
          {hitData && !isWaiting && (
            <LandingVis
              data-testid="pitch-tagging-landing-point-chart"
              className="h-28 w-auto"
              xFeet={hitData.yt_GroundLocationXFeet}
              yFeet={hitData.yt_GroundLocationYFeet}
            />
          )}
          <div className="relative">
            <StrikeZone
              data-testid="pitch-tagging-strike-zone-chart"
              className="h-32 w-auto"
              ballX={ball?.ballX}
              ballY={ball?.ballY}
              isStrike={ball?.isStrike}
              isSoftball={isSoftball}
            />
            {isWaiting && (
              // Centered on the zone box (not the plate) within the SVG viewBox
              <Loader2 className="absolute left-[51%] top-[39%] h-6 w-6 -translate-x-1/2 -translate-y-1/2 animate-spin text-tagging-muted" />
            )}
          </div>
        </div>
      </div>

      {/* Pinned to the panel's corner, outside the centered content */}
      <div className="absolute right-1 top-2">
        <DropdownMenu
          data-testid="pitch-tagging-actions-menu"
          // Non-modal, so the End Game dialog it opens can take focus
          modal={false}
          variant={theme}
          align="end"
          items={[{ label: "End Game", onClick: onEndGame }]}
          trigger={
            <button
              type="button"
              data-testid="pitch-tagging-actions-menu-button"
              aria-label="Game actions"
              // Same size as the Post At-Bat actions button (Button icon-sm)
              className="flex h-8 w-8 items-center justify-center rounded-md text-gray-900 dark:text-white"
            >
              <EllipsisVertical className="h-5 w-5" />
            </button>
          }
        />
      </div>
    </div>
  );
};

const PlayerLine = ({
  label,
  color,
  player,
  "data-testid": testId,
}: {
  label: string;
  color: string;
  player: GamePlayerRef | null;
  "data-testid": string;
}) => (
  <div
    data-testid={testId}
    data-player-id={player?.playerId}
    data-value={player?.playerName}
    className="truncate text-gray-900 dark:text-white"
  >
    <span style={{ color }}>{label}</span> -{" "}
    {formatPlayerName(player?.playerName) || "--"}
  </div>
);

/** "2S Fastball · 72 MPH · Exit Velo 101 MPH", leaving out what's missing */
const PitchInfo = ({
  pitchType,
  pitchVelocity,
  exitVelocity,
}: {
  pitchType: string | null;
  pitchVelocity: number | null;
  exitVelocity: number | null;
}) => {
  const parts: ReactNode[] = [];
  if (pitchType) {
    parts.push(
      <span
        key="type"
        data-testid="pitch-tagging-pitch-type-value"
        data-value={pitchType}
      >
        {pitchType}
      </span>,
    );
  }
  if (pitchVelocity != null) {
    parts.push(
      <span
        key="velo"
        data-testid="pitch-tagging-release-speed-value"
        data-value={pitchVelocity}
      >
        {formatMph(pitchVelocity)} MPH
      </span>,
    );
  }
  if (exitVelocity != null) {
    parts.push(
      <span
        key="exit"
        data-testid="pitch-tagging-exit-speed-value"
        data-value={exitVelocity}
      >
        Exit Velo {formatMph(exitVelocity)} MPH
      </span>,
    );
  }

  return (
    <div className="text-sm text-gray-900 dark:text-white">
      {parts.length
        ? parts.flatMap((part, i) => (i ? [" · ", part] : [part]))
        : "--"}
    </div>
  );
};
